import { useState, useEffect, useCallback, useRef } from "react";
import { useOnlineStatus } from "./useOnlineStatus";
import {
  getAllQueued,
  enqueueAction,
  removeAction,
  updateAction,
  type QueuedAction,
} from "@/lib/offlineQueue";
import { processQueue } from "@/lib/offlineQueueProcessor";
import { toast } from "sonner";

export function useOfflineQueue() {
  const isOnline = useOnlineStatus();
  const [queue, setQueue] = useState<QueuedAction[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const syncingRef = useRef(false);

  const refreshQueue = useCallback(async () => {
    try {
      const items = await getAllQueued();
      setQueue(items);
    } catch {
      // IndexedDB may not be available
    }
  }, []);

  useEffect(() => {
    refreshQueue();
    const handler = () => refreshQueue();
    window.addEventListener("offline-queue-changed", handler);
    return () => window.removeEventListener("offline-queue-changed", handler);
  }, [refreshQueue]);

  const runSync = useCallback(async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    setIsSyncing(true);
    try {
      const { synced, failed, skipped } = await processQueue();
      if (synced > 0) {
        toast.success(`Synced ${synced} queued action${synced > 1 ? "s" : ""}`);
      }
      if (failed > 0) {
        toast.error(`${failed} action${failed > 1 ? "s" : ""} failed to sync`);
      }
      if (skipped > 0 && synced === 0 && failed === 0) {
        toast.info(`${skipped} item${skipped > 1 ? "s are" : " is"} waiting for retry`);
      }
    } catch {
      toast.error("Queue sync failed");
    } finally {
      syncingRef.current = false;
      setIsSyncing(false);
      await refreshQueue();
    }
  }, [refreshQueue]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (!isOnline) return;
    (async () => {
      const items = await getAllQueued();
      if (items.length === 0) return;
      toast.info("Syncing queued actions…");
      await runSync();
    })();
  }, [isOnline, runSync]);

  const addToQueue = useCallback(
    async (action: Omit<QueuedAction, "id" | "createdAt" | "retries">) => {
      const item = await enqueueAction(action);
      toast.info(`"${action.label}" queued — will sync when online`);
      return item;
    },
    []
  );

  /** Manually retry a single failed item — clears backoff and manual-resolution flag. */
  const retryItem = useCallback(async (id: string) => {
    const items = await getAllQueued();
    const item = items.find((x) => x.id === id);
    if (!item) return;
    item.needsManualResolution = false;
    item.nextRetryAt = undefined;
    await updateAction(item);
    await runSync();
  }, [runSync]);

  /** Permanently discard a queued item. */
  const discardItem = useCallback(async (id: string) => {
    await removeAction(id);
    toast.info("Queued scan discarded");
  }, []);

  return {
    queue,
    queueCount: queue.length,
    failedQueue: queue.filter((q) => q.needsManualResolution),
    isSyncing,
    isOnline,
    addToQueue,
    refreshQueue,
    retryItem,
    discardItem,
    runSync,
  };
}
